/**
 * Small shared geometry helpers for validation rules. Kept deliberately
 * simple (axis-aligned bounding circles/boxes) — this app does not need
 * precise polygon collision, just "are these two things too close."
 */

import type { LayoutObject, ObjectCatalogEntry } from '../../types';
import { getCatalogEntry } from '../../inventory';
import { FOOTPRINT } from './clearance';

export function distanceBetween(a: LayoutObject, b: LayoutObject): number {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return Math.sqrt(dx * dx + dy * dy);
}

/** Approximate effective radius of an object's footprint for clearance math. */
export function effectiveRadius(obj: LayoutObject, catalog: ObjectCatalogEntry[]): number {
  if (obj.type === 'round_table') {
    return FOOTPRINT.ROUND_TABLE_WITH_CHAIRS_FT / 2;
  }
  const entry = getCatalogEntry(obj.type, catalog);
  return Math.max(entry.defaultWidthFt, entry.defaultHeightFt) / 2;
}

/** Bounding box for an object, ignoring rotation (sufficient for the
 * "is this inside the room / inside an exclusion zone" checks V1 needs). */
export function boundingBox(
  obj: LayoutObject,
  catalog: ObjectCatalogEntry[]
): { x: number; y: number; width: number; height: number } {
  const entry = getCatalogEntry(obj.type, catalog);
  const width = obj.rotation % 180 === 90 ? entry.defaultHeightFt : entry.defaultWidthFt;
  const height = obj.rotation % 180 === 90 ? entry.defaultWidthFt : entry.defaultHeightFt;
  return { x: obj.x, y: obj.y, width, height };
}

export function boxesOverlap(
  a: { x: number; y: number; width: number; height: number },
  b: { x: number; y: number; width: number; height: number }
): boolean {
  return !(
    a.x + a.width <= b.x ||
    b.x + b.width <= a.x ||
    a.y + a.height <= b.y ||
    b.y + b.height <= a.y
  );
}
